/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import { ReactSelectOption } from "../../../Interfaces";
import Select, { MultiValue } from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { useCreateCognitoUserMutation } from "../../../../services/serverless";
import { useLazyTSUserCreationQuery } from "../../../../services/gallus";
import { getNameFromEmail } from "../../../../helpers/miscHelpers";

interface AddUserModalProps {
  modalIsOpen: boolean;
  preferredUsernameIsEmail: boolean;
  defaultCognitoGroup: string;
  availableCognitoGroups: ReactSelectOption[];
  toggleModal: () => void;
}

export const AddUserModal = (props: AddUserModalProps) => {
  const [newUserEmailAddress, setNewUserEmailAddress] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [newUserGroups, setNewUserGroups] = useState<ReactSelectOption[]>([]);
  const [familyName, setFamilyName] = useState<string>("");
  const [givenName, setGivenName] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string | null>();

  const [createCognitoUserMutation] = useCreateCognitoUserMutation();
  const [TSUserCreation] = useLazyTSUserCreationQuery();

  const handleCreate = async () => {
    try {
      setLoading(true);
      const usernameForNewUser = getNameFromEmail(newUserEmailAddress);
      if (usernameForNewUser !== null) {
        if (props.preferredUsernameIsEmail) {
          await TSUserCreation({
            username: newUserEmailAddress,
            email: newUserEmailAddress,
            orgName: props.defaultCognitoGroup,
          }).unwrap();
          await createCognitoUserMutation({
            username: usernameForNewUser,
            preferredUsername: newUserEmailAddress,
            email: newUserEmailAddress,
            family_name: familyName,
            given_name: givenName,
            groups: [
              props.defaultCognitoGroup,
              ...newUserGroups.map((group) => {
                return group.value;
              }),
            ],
          }).unwrap();
        } else {
          await TSUserCreation({
            username: usernameForNewUser,
            email: newUserEmailAddress,
            orgName: props.defaultCognitoGroup,
          }).unwrap();
          await createCognitoUserMutation({
            username: usernameForNewUser,
            email: newUserEmailAddress,
            family_name: familyName,
            given_name: givenName,
            groups: [
              props.defaultCognitoGroup,
              ...newUserGroups.map((group) => {
                return group.value;
              }),
            ],
          }).unwrap();
        }

        props.toggleModal();
        setLoading(false);
      } else {
        setLoading(false);
        setErrorMessage(`Invalid email.`);
      }
    } catch (error: any) {
      setLoading(false);
      switch (error.message) {
        case "400":
          setErrorMessage(
            `Email address is already in use. Please use another email address.`,
          );
          break;
        case "403":
          setErrorMessage(`Unauthorized action. Please log out and try again.`);
          break;
        default:
          setErrorMessage(`Error creating user. Try again later.`);
          break;
      }
    }
  };

  const resetValues = () => {
    setNewUserEmailAddress("");
    setNewUserGroups([]);
    setFamilyName("");
    setGivenName("");
  };

  useEffect(() => {
    setLoading(false);
  }, []);

  return (
    <Modal isOpen={props.modalIsOpen} toggle={props.toggleModal}>
      <ModalHeader toggle={props.toggleModal}>Create User</ModalHeader>
      {loading && (
        <div
          style={{
            paddingBottom: "25px",
          }}
          className="loading-div"
        >
          <FontAwesomeIcon
            icon={faSpinner}
            size="2x"
            spin={true}
            style={{ margin: "0 auto" }}
          />
        </div>
      )}
      {!errorMessage && !loading && (
        <div>
          <ModalBody>
            <div>
              <div
                style={{
                  fontSize: "larger",
                  paddingBottom: "5px",
                }}
              >
                Please provide user details
                <hr></hr>
              </div>
              <div>
                <div>First Name</div>
                <input
                  type="text"
                  className="form-control formSignUpUsername"
                  value={givenName}
                  onChange={(firstNameInput) => {
                    setGivenName(firstNameInput.target.value);
                  }}
                />
                <div>Last Name</div>
                <input
                  type="text"
                  className="form-control formSignUpUsername"
                  value={familyName}
                  onChange={(lastNameInput) => {
                    setFamilyName(lastNameInput.target.value);
                  }}
                />
                <div>Email Address</div>
                <input
                  type="text"
                  className="form-control formSignUpUsername"
                  value={newUserEmailAddress}
                  onChange={(emailInput) => {
                    setNewUserEmailAddress(emailInput.target.value);
                  }}
                />
                <div>Additional access groups</div>
                <Select
                  defaultValue={newUserGroups}
                  onChange={(
                    selectedOptions: MultiValue<ReactSelectOption>,
                  ) => {
                    if (Array.isArray(selectedOptions)) {
                      setNewUserGroups(selectedOptions);
                    }
                  }}
                  options={props.availableCognitoGroups}
                  isMulti={true}
                  className="basic-multi-select card-text"
                  classNamePrefix="select"
                  closeMenuOnSelect={false}
                />
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              color="primary"
              disabled={
                newUserEmailAddress.length === 0 ||
                getNameFromEmail(newUserEmailAddress) === null
              }
              onClick={() => {
                handleCreate();
                resetValues();
              }}
            >
              Create user
            </Button>
            <Button
              color="secondary"
              onClick={() => {
                props.toggleModal();
                resetValues();
              }}
            >
              Cancel
            </Button>
          </ModalFooter>
        </div>
      )}
      {errorMessage && !loading && (
        <div>
          <ModalBody>{errorMessage}</ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              onClick={() => {
                resetValues();
                setErrorMessage(null);
                props.toggleModal();
              }}
            >
              Close
            </Button>
          </ModalFooter>
        </div>
      )}
    </Modal>
  );
};
